import * as React from 'react';
import { NavigationApplyClick } from '../../Navigation/Events/NavigationApplyClick';
import styles from '../AdmissionCallToAction.module.css';
import type { AdmissionCallToActionStatus } from '../AdmissionCallToActionStatus';
import { AdmissionCallToActionOverlayStatusChanged } from '../Events/AdmissionCallToActionOverlayStatusChanged';
import type { ICampusApplicationProcessState } from '~/Features/CampusApplication/CampusApplicationProcessState';
import { CampusApplicationProcessState } from '~/Features/CampusApplication/CampusApplicationProcessState';
import { areAllItemsAdded, isItemAdded } from '~/Features/CampusApplication/Utils/campusApplicationUtils';
import { MessageBroker } from '~/Features/Messaging/MessageBroker';
import { useMessageHandler } from '~/Features/Messaging/useMessageHandler';
import type { ApplicationChoiceDto } from '~/Features/Search/ApplicationChoice/Controller/ApplicationChoiceDto.csharp';
import { WithOverlay } from '~/Features/StudyPage/Campus/Components/Overlay/WithOverlay';
import { Button } from '~/Partials/Button/Button';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { redirect } from '~/Partials/Utils/utils';
import type { Theme } from '~/Utils/Theme';

interface AdmissionCallToActionOverlayProps {
    heading: string;
    closeLabel: string;
    buttonLabel: string;
    theme: Theme;
    choices: ApplicationChoiceDto[];
    campusApplicationUrl: string;
    status?: AdmissionCallToActionStatus;
}

export const AdmissionCallToActionOverlay: React.FC<
    React.PropsWithChildren<AdmissionCallToActionOverlayProps>
> = props => {
    const { heading, buttonLabel, closeLabel, status, theme, campusApplicationUrl, choices, children } = props;

    const [shouldDisableButton, setShouldDisableButton] = React.useState(false);
    const [processState, setProcessState] = React.useState<ICampusApplicationProcessState | null>(null);
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    const isButtonDisabled = status !== 'open' || shouldDisableButton || choices.length === 0;

    React.useEffect(() => {
        const applicationProcess = CampusApplicationProcessState();
        setProcessState(applicationProcess);
    }, []);

    React.useEffect(() => {
        if (processState) {
            if (processState.isOnlineApplicationInProgress() || processState.isItemsLimitReached()) {
                setShouldDisableButton(true);

                return;
            }

            const currentChoices = processState.getAll();

            const isProductMultiVariant = choices.length > 1;

            if (isProductMultiVariant) {
                const areAllVariantsAlreadyAdded = areAllItemsAdded(choices, currentChoices);
                setShouldDisableButton(areAllVariantsAlreadyAdded);

                return;
            }

            const isProductSingleVariant = choices.length === 1;

            if (isProductSingleVariant) {
                const isVariantAlreadyAdded = isItemAdded(choices[0].id, currentChoices);
                setShouldDisableButton(isVariantAlreadyAdded);

                return;
            }

            setShouldDisableButton(false);
        }
    }, [choices, processState]);

    useMessageHandler(NavigationApplyClick.id, () => {
        buttonRef.current && buttonRef.current.click();
    });

    React.useEffect(() => {
        MessageBroker.schedulePublish(new AdmissionCallToActionOverlayStatusChanged('disabled', isButtonDisabled), 0);
    }, [isButtonDisabled]);

    const handleClick = (overlayClick: () => void) => {
        if (processState && choices.length === 1) {
            const [applicationChoice] = choices;
            const isSuccessfullyAdded = processState.append(applicationChoice);

            if (isSuccessfullyAdded) {
                redirect(campusApplicationUrl);

                return;
            }

            return;
        }

        overlayClick();
    };

    return (
        <WithOverlay
            overlayLabel={heading}
            overlayContent={
                <>
                    <h2>{heading}</h2>
                    {children}
                </>
            }
            closeLabel={closeLabel}
        >
            {buttonProps => (
                <Button
                    {...buttonProps}
                    ref={buttonRef}
                    disabled={isButtonDisabled}
                    className={styles.Action}
                    size={ButtonSize.XLarge}
                    type={ButtonType.Solid}
                    tier={ButtonTier.Secondary}
                    theme={theme}
                    title={buttonLabel}
                    onClick={() => handleClick(buttonProps.onClick)}
                >
                    {buttonLabel}
                    <SvgIcon iconName="arrow-right" />
                </Button>
            )}
        </WithOverlay>
    );
};
